import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondary,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Badge,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Rating } from '@material-ui/lab';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  PhotoLibrary as PhotoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  Star as StarIcon,
  Comment as CommentIcon,
  Close as CloseIcon,
  BrokenImage as BrokenImageIcon,
} from '@material-ui/icons';
import CircularProgress from '@material-ui/core/CircularProgress';
import moment from 'moment';
import BigImageComponent from '../Uploads/BigImageComponent';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
  },
  header: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statsGrid: {
    marginBottom: theme.spacing(3),
  },
  statCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4],
    },
  },
  statValue: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  filterSection: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: 'white',
    borderRadius: theme.shape.borderRadius,
  },
  reviewCard: {
    marginBottom: theme.spacing(2),
    cursor: 'pointer',
    transition: 'all 0.3s',
    '&:hover': {
      boxShadow: theme.shadows[6],
    },
  },
  reviewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
  },
  reviewTypeBadge: {
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    fontWeight: 'bold',
    fontSize: '0.75rem',
  },
  siteReview: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
  },
  crewReview: {
    backgroundColor: '#f3e5f5',
    color: '#7b1fa2',
  },
  bothReview: {
    background: 'linear-gradient(135deg, #e3f2fd 50%, #f3e5f5 50%)',
    color: '#5e35b1',
  },
  ratingSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  issueChip: {
    margin: theme.spacing(0.5),
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
  actionChip: {
    margin: theme.spacing(0.5),
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  photoThumbnail: {
    width: 60,
    height: 60,
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    margin: theme.spacing(0.5),
  },
  expandedContent: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  lqaSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  tabPanel: {
    marginTop: theme.spacing(2),
  },
}));

// Photo Thumbnail Component with proper error handling
const PhotoThumbnail = ({ photo, index, onClick, classes }) => {
  const [imageError, setImageError] = useState(false);
  const [loading, setLoading] = useState(true);

  const photoUrl = typeof photo === 'string' ? photo : (photo.uri || photo.url || photo);

  if (imageError) {
    return (
      <Box
        className={classes.photoThumbnail}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          border: '2px solid #e0e0e0',
          borderRadius: 8,
          margin: 4,
          cursor: 'pointer',
        }}
        onClick={onClick}
      >
        <Box textAlign="center">
          <BrokenImageIcon style={{ color: '#bdbdbd', fontSize: 24 }} />
          <Typography variant="caption" style={{ color: '#999', display: 'block', fontSize: 10 }}>
            Image {index + 1}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      style={{
        position: 'relative',
        margin: 4,
        border: '2px solid #e0e0e0',
        borderRadius: 8,
        overflow: 'hidden',
        cursor: 'pointer',
        backgroundColor: loading ? '#f5f5f5' : 'transparent',
      }}
      onClick={onClick}
    >
      {loading && (
        <Box
          className={classes.photoThumbnail}
          style={{
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
          }}
        >
          <CircularProgress size={20} />
        </Box>
      )}
      <img
        src={photoUrl}
        alt={`Review photo ${index + 1}`}
        className={classes.photoThumbnail}
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setImageError(true);
        }}
        style={{ display: loading ? 'none' : 'block' }}
      />
    </Box>
  );
};

const SiteReviewDashboardV2 = ({ data = [], initialIssueId = '' }) => {
  const classes = useStyles();
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [expandedReviews, setExpandedReviews] = useState({});
  const [selectedReview, setSelectedReview] = useState(null);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);

  useEffect(() => {
    // Process and normalize review data
    const processedReviews = data.map(review => {
      // Determine review type
      let reviewType = review.reviewType || 'site';
      if (review.crewData && review.siteData) {
        reviewType = 'both';
      } else if (review.crewData && !review.siteData) {
        reviewType = 'crew';
      }

      // Extract ratings based on structure
      let ratings = {};
      if (review.siteData?.ratings) {
        ratings = review.siteData.ratings;
      } else if (review.reviewData && Array.isArray(review.reviewData)) {
        // Handle old format if needed
        review.reviewData.forEach(item => {
          if (item.name && item.rating) {
            ratings[item.name] = item.rating;
          }
        });
      }

      // Extract photos
      let photos = [];
      if (review.siteData?.photos) {
        photos = review.siteData.photos;
      } else if (review.photos) {
        photos = review.photos;
      } else if (review.uploads) {
        photos = review.uploads;
      }

      return {
        ...review,
        reviewType,
        ratings,
        photos,
        issues: review.siteData?.issues || review.issues || [],
        actionItems: review.siteData?.actionItems || review.actionItems || [],
      };
    });

    setReviews(processedReviews);
    setFilteredReviews(processedReviews);
    if (initialIssueId) {
      const requestedReview = processedReviews.find(review => {
        const issues = review.issues || [];
        return issues.some(issue => String(issue?._id || issue?.id || '') === initialIssueId);
      });
      if (requestedReview?._id) {
        setExpandedReviews(current => ({ ...current, [requestedReview._id]: true }));
      }
    }
  }, [data, initialIssueId]);

  useEffect(() => {
    filterReviews();
  }, [searchTerm, filterType, selectedTab, reviews]);

  const filterReviews = () => {
    let filtered = [...reviews];

    // Filter by tab
    if (selectedTab === 1) {
      filtered = filtered.filter(r => r.reviewType === 'site');
    } else if (selectedTab === 2) {
      filtered = filtered.filter(r => r.reviewType === 'crew');
    } else if (selectedTab === 3) {
      filtered = filtered.filter(r => r.reviewType === 'both');
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(review =>
        review.jobSite?.name?.toLowerCase().includes(term) ||
        review.jobSite?.address?.toLowerCase().includes(term) ||
        review.crewData?.crewName?.toLowerCase().includes(term) ||
        review.created_by?.toLowerCase().includes(term) ||
        review.reviewedByName?.toLowerCase().includes(term)
      );
    }

    // Filter by issues/no issues
    if (filterType === 'issues') {
      filtered = filtered.filter(r => r.issues && r.issues.length > 0);
    } else if (filterType === 'no-issues') {
      filtered = filtered.filter(r => !r.issues || r.issues.length === 0);
    }

    setFilteredReviews(filtered);
  };

  const calculateStats = () => {
    const stats = {
      total: reviews.length,
      site: reviews.filter(r => r.reviewType === 'site').length,
      crew: reviews.filter(r => r.reviewType === 'crew').length,
      both: reviews.filter(r => r.reviewType === 'both').length,
      withIssues: reviews.filter(r => r.issues && r.issues.length > 0).length,
      avgRating: 0,
    };

    // Calculate average rating
    let totalRatings = 0;
    let ratingCount = 0;
    reviews.forEach(review => {
      if (review.ratings) {
        Object.values(review.ratings).forEach(rating => {
          if (rating > 0) {
            totalRatings += rating;
            ratingCount++;
          }
        });
      }
    });
    stats.avgRating = ratingCount > 0 ? (totalRatings / ratingCount).toFixed(1) : 0;

    return stats;
  };

  const handleExpandReview = (reviewId) => {
    setExpandedReviews(prev => ({
      ...prev,
      [reviewId]: !prev[reviewId]
    }));
  };

  const handleViewPhotos = (review) => {
    // Ensure photos are properly formatted for BigImageComponent
    const formattedPhotos = (review.photos || []).map(photo => {
      if (typeof photo === 'string') {
        return photo;
      }
      return photo.uri || photo.url || photo;
    });
    setSelectedImages(formattedPhotos);
    setSelectedReview(review);
    setShowImageViewer(true);
  };

  const openLocationInMaps = (location) => {
    if (location && location.latitude && location.longitude) {
      // Open in Google Maps
      const url = `https://www.google.com/maps?q=${location.latitude},${location.longitude}`;
      window.open(url, '_blank');
    }
  };

  const renderRatingSection = (ratings) => {
    if (!ratings || Object.keys(ratings).length === 0) return null;

    return (
      <Grid container spacing={2}>
        {Object.entries(ratings).map(([category, rating]) => (
          <Grid item xs={6} sm={4} md={2} key={category}>
            <Box textAlign="center">
              <Typography variant="caption" color="textSecondary">
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </Typography>
              <Rating value={rating} size="small" readOnly />
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderLQASection = (crewData) => {
    if (!crewData || !crewData.lqaResponses) return null;

    return (
      <Box className={classes.lqaSection}>
        <Typography variant="h6" gutterBottom>
          <GroupIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Crew Assessment - {crewData.crewName}
        </Typography>
        <Grid container spacing={2}>
          {crewData.ratings && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Crew Performance</Typography>
              <Box display="flex" gap={2}>
                <Chip label={`Receptiveness: ${crewData.ratings.receptiveness}/3`} />
                <Chip label={`On Task: ${crewData.ratings.onTask}/3`} />
                <Chip label={`Attitude: ${crewData.ratings.attitude}/3`} />
              </Box>
            </Grid>
          )}
          {Object.entries(crewData.lqaResponses || {}).map(([question, response]) => (
            <Grid item xs={12} sm={6} key={question}>
              <Typography variant="body2" color="textSecondary">
                {question}
              </Typography>
              <Typography variant="body1">
                {typeof response === 'boolean' ? (response ? 'Yes' : 'No') : response}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderReviewCard = (review) => {
    const isExpanded = expandedReviews[review._id];
    const reviewTypeClass =
      review.reviewType === 'both' ? classes.bothReview :
      review.reviewType === 'crew' ? classes.crewReview :
      classes.siteReview;

    return (
      <Card key={review._id} className={classes.reviewCard}>
        <CardContent>
          {/* Header */}
          <Box className={classes.reviewHeader}>
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Chip
                  label={review.reviewType.toUpperCase()}
                  className={`${classes.reviewTypeBadge} ${reviewTypeClass}`}
                  size="small"
                />
                <Typography variant="h6">
                  {review.jobSite?.name || 'Unknown Site'}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                <LocationIcon style={{ fontSize: 16, verticalAlign: 'middle' }} />
                {review.jobSite?.address || 'No address'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <PersonIcon style={{ fontSize: 16, verticalAlign: 'middle' }} />
                Reviewed by {review.reviewedByName || review.created_by || 'Unknown'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <TimeIcon style={{ fontSize: 16, verticalAlign: 'middle' }} />
                {moment(review.submitted_at || review.created_at).format('MMM DD, YYYY h:mm A')}
              </Typography>
            </Box>
            <Box>
              <IconButton onClick={() => handleExpandReview(review._id)}>
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>

          {/* Quick Stats */}
          <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
            {review.issues && review.issues.length > 0 && (
              <Tooltip title={`${review.issues.filter(i => i.location).length} with location data`}>
                <Chip
                  icon={<WarningIcon />}
                  label={`${review.issues.length} Issues`}
                  className={classes.issueChip}
                  size="small"
                  onClick={() => handleExpandReview(review._id)}
                />
              </Tooltip>
            )}
            {review.actionItems && review.actionItems.length > 0 && (
              <Chip
                icon={<AssignmentIcon />}
                label={`${review.actionItems.length} Action Items`}
                className={classes.actionChip}
                size="small"
                onClick={() => handleExpandReview(review._id)}
              />
            )}
            {review.photos && review.photos.length > 0 && (
              <Tooltip title="Click to view photos">
                <Chip
                  icon={<PhotoIcon />}
                  label={`${review.photos.length} Photos`}
                  onClick={() => handleViewPhotos(review)}
                  size="small"
                  color="primary"
                  variant="outlined"
                  style={{ cursor: 'pointer' }}
                />
              </Tooltip>
            )}
            {review.issues && review.issues.some(i => i.location) && (
              <Tooltip title="Has GPS coordinates">
                <Chip
                  icon={<LocationIcon />}
                  label="GPS Data"
                  size="small"
                  variant="outlined"
                  style={{ borderColor: '#4caf50', color: '#4caf50' }}
                />
              </Tooltip>
            )}
          </Box>

          {/* Site Ratings */}
          {review.ratings && Object.keys(review.ratings).length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>Site Ratings</Typography>
              {renderRatingSection(review.ratings)}
            </Box>
          )}

          {/* Expanded Content */}
          <Collapse in={isExpanded}>
            <Box className={classes.expandedContent}>
              {/* Issues */}
              {review.issues && review.issues.length > 0 && (
                <Box mb={2}>
                  <Typography variant="h6" gutterBottom>Issues Identified</Typography>
                  <List>
                    {review.issues.map((issue, idx) => (
                      <ListItem key={idx}>
                        <ListItemAvatar>
                          <Avatar style={{ backgroundColor: '#f44336' }}>
                            <WarningIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={issue.label || issue.description}
                          secondary={
                            <>
                              {issue.notes && <Typography variant="body2">{issue.notes}</Typography>}
                              {issue.location && (
                                <Box display="flex" alignItems="center" mt={1}>
                                  <LocationIcon style={{ fontSize: 16, marginRight: 4, color: '#1976d2' }} />
                                  <Typography
                                    variant="caption"
                                    style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                                    onClick={() => openLocationInMaps(issue.location)}
                                  >
                                    {issue.location.latitude?.toFixed(6)}, {issue.location.longitude?.toFixed(6)}
                                    (Click to view on map)
                                  </Typography>
                                </Box>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Action Items */}
              {review.actionItems && review.actionItems.length > 0 && (
                <Box mb={2}>
                  <Typography variant="h6" gutterBottom>Action Items</Typography>
                  <List>
                    {review.actionItems.map((item, idx) => (
                      <ListItem key={idx}>
                        <ListItemAvatar>
                          <Avatar style={{ backgroundColor: '#ff9800' }}>
                            <AssignmentIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={item.title || item.description}
                          secondary={
                            <>
                              {item.assignedTo && (
                                <Chip
                                  size="small"
                                  label={`Assigned to: ${item.assignedTo}`}
                                  style={{ marginRight: 8 }}
                                />
                              )}
                              {item.priority && (
                                <Chip
                                  size="small"
                                  label={`Priority: ${item.priority}`}
                                  color={item.priority === 'high' ? 'secondary' : 'default'}
                                />
                              )}
                              {item.dueDate && (
                                <Typography variant="caption" display="block">
                                  Due: {moment(item.dueDate).format('MMM DD, YYYY')}
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Crew LQA Section */}
              {review.crewData && renderLQASection(review.crewData)}

              {/* Photos Preview */}
              {review.photos && review.photos.length > 0 && (
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>
                    <PhotoIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    Photos ({review.photos.length})
                  </Typography>
                  <Box display="flex" flexWrap="wrap">
                    {review.photos.slice(0, 6).map((photo, idx) => (
                      <PhotoThumbnail
                        key={idx}
                        photo={photo}
                        index={idx}
                        onClick={() => handleViewPhotos(review)}
                        classes={classes}
                      />
                    ))}
                    {review.photos.length > 6 && (
                      <Box
                        className={classes.photoThumbnail}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        style={{
                          backgroundColor: '#e0e0e0',
                          cursor: 'pointer',
                          margin: 4,
                          borderRadius: 8,
                          fontWeight: 'bold'
                        }}
                        onClick={() => handleViewPhotos(review)}
                      >
                        <Typography variant="body2">+{review.photos.length - 6} more</Typography>
                      </Box>
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => handleViewPhotos(review)}
                    startIcon={<PhotoIcon />}
                    style={{ marginTop: 8 }}
                  >
                    View All Photos
                  </Button>
                </Box>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  const stats = calculateStats();

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h4" component="h1">
          Site & Crew Reviews
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Last updated: {moment().format('MMM DD, YYYY h:mm A')}
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} className={classes.statsGrid}>
        <Grid item xs={6} sm={3}>
          <Card className={classes.statCard}>
            <Typography className={classes.statValue}>{stats.total}</Typography>
            <Typography className={classes.statLabel}>Total Reviews</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card className={classes.statCard}>
            <Typography className={classes.statValue}>{stats.avgRating}</Typography>
            <Rating value={parseFloat(stats.avgRating)} readOnly />
            <Typography className={classes.statLabel}>Avg Rating</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card className={classes.statCard}>
            <Typography className={classes.statValue}>{stats.withIssues}</Typography>
            <Typography className={classes.statLabel}>With Issues</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card className={classes.statCard}>
            <Box display="flex" gap={1}>
              <Chip label={`Site: ${stats.site}`} size="small" />
              <Chip label={`Crew: ${stats.crew}`} size="small" />
              <Chip label={`Both: ${stats.both}`} size="small" />
            </Box>
            <Typography className={classes.statLabel}>By Type</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Paper className={classes.filterSection}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by site, crew, or reviewer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Filter</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                label="Filter"
              >
                <MenuItem value="all">All Reviews</MenuItem>
                <MenuItem value="issues">With Issues</MenuItem>
                <MenuItem value="no-issues">No Issues</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={`All (${stats.total})`} />
          <Tab label={`Site Only (${stats.site})`} />
          <Tab label={`Crew Only (${stats.crew})`} />
          <Tab label={`Combined (${stats.both})`} />
        </Tabs>
      </Paper>

      {/* Reviews List */}
      <Box className={classes.tabPanel}>
        {filteredReviews.length === 0 ? (
          <Paper style={{ padding: 32, textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary">
              No reviews found
            </Typography>
          </Paper>
        ) : (
          filteredReviews.map(review => renderReviewCard(review))
        )}
      </Box>

      {/* Image Viewer Modal */}
      <BigImageComponent
        open={showImageViewer}
        item={selectedReview}
        images={selectedImages}
        closeModal={() => {
          setShowImageViewer(false);
          setSelectedImages([]);
          setSelectedReview(null);
        }}
      />
    </Box>
  );
};

export default SiteReviewDashboardV2;
